import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { Download, X, Smartphone, Monitor, Share } from 'lucide-react';
import { Button } from './ui/button';

const PWAInstallPrompt = () => {
  const { theme } = useTheme();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    setIsStandalone(standalone);
    if (standalone) return;

    const dismissed = localStorage.getItem('beatlab_pwa_dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    } else if (isIOS) {
      setShowIOSGuide(true);
    } else {
      handleDismiss();
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSGuide(false);
    localStorage.setItem('beatlab_pwa_dismissed', Date.now().toString());
  };

  if (isStandalone) return null;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 80 }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm z-[60] rounded-2xl p-4 shadow-2xl border"
          style={{
            background: 'linear-gradient(135deg, rgba(10,10,10,0.98), rgba(20,20,20,0.95))',
            borderColor: `${theme.primary}30`,
            backdropFilter: 'blur(20px)',
          }}
        >
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/10 transition"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>

          {!showIOSGuide ? (
            <div className="flex items-start gap-3">
              <div
                className="p-2.5 rounded-xl flex-shrink-0"
                style={{ backgroundColor: `${theme.primary}15` }}
              >
                <Download className="w-6 h-6" style={{ color: theme.primary }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-white text-sm mb-1">Install BeatLab52</h3>
                <p className="text-xs text-gray-400 mb-3">
                  Get the app on your device for the best experience — works offline too!
                </p>
                <div className="flex items-center gap-2 mb-3">
                  <Smartphone className="w-3.5 h-3.5 text-gray-500" />
                  <Monitor className="w-3.5 h-3.5 text-gray-500" />
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider">iPhone · Android · Desktop</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleInstall}
                    className="flex-1 font-bold text-xs py-2"
                    style={{ backgroundColor: theme.primary, color: '#000' }}
                  >
                    <Download className="w-3.5 h-3.5 mr-1" />
                    {isIOS ? 'How to Install' : 'Install App'}
                  </Button>
                  <Button
                    onClick={handleDismiss}
                    variant="ghost"
                    className="text-xs py-2 text-gray-400"
                  >
                    Later
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <h3 className="font-bold text-white text-sm mb-3">Install on iPhone/iPad</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ backgroundColor: `${theme.primary}20`, color: theme.primary }}>1</div>
                  <p className="text-xs text-gray-300">
                    Tap the <Share className="w-3.5 h-3.5 inline" style={{ color: theme.primary }} /> <strong>Share</strong> button at the bottom of Safari
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ backgroundColor: `${theme.primary}20`, color: theme.primary }}>2</div>
                  <p className="text-xs text-gray-300">
                    Scroll down and tap <strong>"Add to Home Screen"</strong>
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ backgroundColor: `${theme.primary}20`, color: theme.primary }}>3</div>
                  <p className="text-xs text-gray-300">
                    Tap <strong>"Add"</strong> — BeatLab52 will appear on your home screen!
                  </p>
                </div>
              </div>
              <Button
                onClick={handleDismiss}
                className="w-full mt-3 font-bold text-xs"
                style={{ backgroundColor: theme.primary, color: '#000' }}
              >
                Got It!
              </Button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PWAInstallPrompt;
